import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { barsApi } from '@/api/endpoints/bars';
import { BarCard } from '@/components/domain';
import { PageSpinner, Card, CardContent, Button } from '@/components/ui';
import { Heart } from 'lucide-react';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import { useAuth } from '@/contexts/AuthContext';

export function FavoritesPage() {
  const { favorites } = useUserPreferences();
  const { isAuthenticated } = useAuth();
  const favoriteIds = useMemo(() => Array.from(favorites), [favorites]);

  // Fetch all favorite bars
  const barQueries = useQueries({
    queries: favoriteIds.map((id) => ({
      queryKey: ['bar', id],
      queryFn: () => barsApi.getById(id),
    })),
  });

  const isLoading = barQueries.some((q) => q.isLoading);
  const bars = barQueries
    .filter((q) => q.data?.bar)
    .map((q) => q.data!.bar);

  // Sort by distance
  const sortedBars = useMemo(() => {
    return [...bars].sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
  }, [bars]);

  if (isLoading && favoriteIds.length > 0) {
    return <PageSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Favorites</h1>
        <p className="mt-1 text-[var(--color-muted)]">
          Your saved bars
        </p>
      </div>

      {/* Sign in prompt for anonymous users */}
      {!isAuthenticated && favoriteIds.length > 0 && (
        <Card>
          <CardContent className="flex items-center justify-between p-4">
            <p className="text-sm text-[var(--color-muted)]">
              Sign in to sync favorites across devices
            </p>
            <Link to="/login">
              <Button variant="secondary" size="sm">
                Sign In
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Favorites list */}
      {sortedBars.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-12 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-primary/10">
              <Heart className="h-8 w-8 text-accent-primary" />
            </div>
            <h2 className="mt-4 text-lg font-semibold">No favorites yet</h2>
            <p className="mt-2 max-w-sm text-sm text-[var(--color-muted)]">
              Start exploring bars and tap the heart icon to save your favorites.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {sortedBars.map((bar) => (
            <BarCard key={bar.id} bar={bar} />
          ))}
        </div>
      )}
    </div>
  );
}
